import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, BookOpen } from "lucide-react";

const navLinks = [
    { href: "/#features", label: "Features" },
    { href: "/shop", label: "The Bag Drop" },
    { href: "/credits", label: "Credits" },
    { href: "/membership", label: "Membership" },
    { href: "/find-a-machine", label: "Find a Machine" },
    { href: "/#faq", label: "FAQ" },
    { href: "/support", label: "Support" },
];

const Logo = () => (
    <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
        <span className="w-10 h-10 rounded-full bg-[#C4956A] flex items-center justify-center text-white shadow-soft group-hover:bg-[#A67C56] transition-colors">
            <BookOpen size={20} strokeWidth={2.2} />
        </span>
        <span className="font-serif text-2xl font-semibold tracking-tight text-[#2D2D2D]">
            BookBlend
        </span>
    </Link>
);

const DesktopNav = () => (
    <>
        <nav className="hidden lg:flex items-center gap-7 mx-8">
            {navLinks.map((l) => (
                <a
                    key={l.href}
                    href={l.href}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                    className="text-[15px] whitespace-nowrap text-[#2D2D2D]/80 hover:text-[#C4956A] transition-colors font-medium"
                >
                    {l.label}
                </a>
            ))}
        </nav>
        <a
            href="#download"
            data-testid="navbar-cta-download"
            className="hidden lg:inline-flex flex-shrink-0 items-center px-6 py-2.5 rounded-full bg-[#2D2D2D] text-[#FDF6EE] text-sm font-medium whitespace-nowrap hover:bg-[#C4956A] transition-colors"
        >
            Download app
        </a>
    </>
);

const MobileMenu = () => (
    <div
        data-testid="navbar-mobile-menu"
        className="lg:hidden bg-[#FDF6EE] border-t border-[#E5D7C5] px-6 py-6 space-y-4"
    >
        {navLinks.map((l) => (
            <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className="block text-[#2D2D2D] text-lg font-medium"
            >
                {l.label}
            </a>
        ))}
        <a
            href="#download"
            data-testid="navbar-mobile-cta"
            className="block w-full text-center px-6 py-3 rounded-full bg-[#2D2D2D] text-[#FDF6EE] font-medium"
        >
            Download app
        </a>
    </div>
);

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#FDF6EE]/85 backdrop-blur-md border-b border-[#E5D7C5]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 flex items-center justify-between h-20">
                <Logo />
                <DesktopNav />
                <button
                    type="button"
                    aria-label="Toggle menu"
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden w-10 h-10 rounded-full bg-white border border-[#E5D7C5] flex items-center justify-center text-[#2D2D2D]"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>
            {open && <MobileMenu />}
        </header>
    );
}
